import { useState, useEffect } from 'react';
import { Plus, Trash2, Search, Save, Pencil, Sparkles, Check, X, Database, FileText, TrendingUp, ChevronDown, ChevronRight } from 'lucide-react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { api } from '../../lib/api';
import Button from '../ui/Button';
import Input from '../ui/Input';
import Textarea from '../ui/Textarea';
import Card from '../ui/Card';
import Spinner from '../ui/Spinner';
import AdditionalDataSuggestionsModal from '../AdditionalDataSuggestionsModal';
import DataSourcingOverview from '../project/DataSourcingOverview';

interface DataRequirementMapping {
  requirementEntity: string;
  requirementCategory: 'master_data' | 'transactional_data';
  confidence: 'high' | 'medium' | 'low';
  reasoning: string;
  typicalLocation?: string;
  matchesExisting?: boolean;
}

interface EnterpriseSystem {
  id: string;
  name: string;
  businessUse: string;
  // Optional AI research data
  generalDescription?: string;
  typicalUseCases?: string[];
  industrySpecificUses?: string;
  keyFunctionalities?: string[];
  integrationPoints?: string[];
  dataRequirementMappings?: DataRequirementMapping[];
}

interface SystemLandscapeFormProps {
  caseId?: string;
  onSave?: (data: EnterpriseSystem[]) => void;
  onComplete?: () => void; // Called when form is ready to move to next step
}

// Standard enterprise systems list
const ENTERPRISE_SYSTEMS = [
  'SAP ERP',
  'SAP S/4HANA',
  'Oracle ERP Cloud',
  'Oracle E-Business Suite',
  'Microsoft Dynamics 365',
  'Salesforce CRM',
  'Salesforce Marketing Cloud',
  'Workday HCM',
  'ServiceNow',
  'Ariba',
  'Coupa',
  'Kinaxis RapidResponse',
  'Blue Yonder (JDA)',
  'Manhattan Associates',
  'Anaplan',
  'NetSuite',
  'Concur',
  'SuccessFactors',
  'Adobe Experience Cloud',
  'HubSpot',
  'Tableau',
  'Power BI',
  'Snowflake',
  'Databricks',
];

export default function SystemLandscapeForm({ caseId, onSave, onComplete }: SystemLandscapeFormProps) {
  const queryClient = useQueryClient();
  const [systems, setSystems] = useState<EnterpriseSystem[]>([]);
  const [aiFeedback, setAiFeedback] = useState<{
    type: 'suggestion' | 'clarification' | 'success';
    message: string;
  } | null>(null);
  const [isResearching, setIsResearching] = useState(false);
  const [showResearchResults, setShowResearchResults] = useState(false);
  const [researchResults, setResearchResults] = useState<any>(null);
  const [selectedMappings, setSelectedMappings] = useState<DataRequirementMapping[]>([]);

  // Track changes
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [initialSystemsLoaded, setInitialSystemsLoaded] = useState(false);

  // Track which systems are expanded
  const [expandedSystems, setExpandedSystems] = useState<Set<string>>(new Set());

  // Fetch existing system landscape if project exists
  const { data: existingLandscape, isLoading } = useQuery({
    queryKey: ['systemLandscape', caseId],
    queryFn: async () => {
      if (!caseId) return null;
      try {
        return await api.get(`/cases/${caseId}/system-landscape`);
      } catch (error: any) {
        if (error?.response?.status === 404) {
          return null;
        }
        throw error;
      }
    },
    enabled: !!caseId,
    retry: false,
  });

  // Load existing landscape data into form
  useEffect(() => {
    if (existingLandscape?.systems) {
      const loadedSystems = existingLandscape.systems.map((sys: any, idx: number) => ({
        id: sys.appId || `${idx}`,
        name: sys.name,
        businessUse: sys.businessUse,
        // Include AI research fields if they exist
        generalDescription: sys.generalDescription,
        typicalUseCases: sys.typicalUseCases,
        industrySpecificUses: sys.industrySpecificUses,
        keyFunctionalities: sys.keyFunctionalities,
        integrationPoints: sys.integrationPoints,
        dataRequirementMappings: sys.dataRequirementMappings
      }));
      setSystems(loadedSystems);
      setInitialSystemsLoaded(true);
      setHasUnsavedChanges(false); // Mark as saved since we just loaded from DB
    }
  }, [existingLandscape]);

  // Save mutation
  const saveMutation = useMutation({
    mutationFn: async (systemsData: EnterpriseSystem[]) => {
      if (!caseId) {
        throw new Error('Project ID is required');
      }
      const payload = {
        systems: systemsData.map(s => ({
          name: s.name,
          businessUse: s.businessUse,
          generalDescription: s.generalDescription,
          typicalUseCases: s.typicalUseCases,
          industrySpecificUses: s.industrySpecificUses,
          keyFunctionalities: s.keyFunctionalities,
          integrationPoints: s.integrationPoints,
          dataRequirementMappings: s.dataRequirementMappings
        }))
      };
      console.log('[SystemLandscape] Saving systems with mappings:', payload);
      return await api.post(`/cases/${caseId}/system-landscape`, payload);
    },
    onSuccess: (data) => {
      toast.success('System landscape saved successfully!');
      queryClient.invalidateQueries({ queryKey: ['systemLandscape', caseId] });
      setHasUnsavedChanges(false); // Reset unsaved changes flag
      // Don't call onSave here - it will be called separately when needed

      // Show AI feedback only if there are systems
      if (systems.length > 0) {
        const feedbackExamples = [
          {
            type: 'suggestion' as const,
            message: 'Great system landscape! Consider adding integration patterns between these systems to better understand data flows.',
          },
          {
            type: 'clarification' as const,
            message: 'I notice you have multiple enterprise systems. Could you clarify which system is the source of truth for customer master data?',
          },
          {
            type: 'success' as const,
            message: 'Excellent system landscape definition! The business use cases are clear and will help us design optimal graph relationships.',
          },
        ];
        setAiFeedback(feedbackExamples[Math.floor(Math.random() * feedbackExamples.length)]);
      } else {
        // Clear AI feedback when saving empty systems list
        setAiFeedback(null);
      }
    },
    onError: (error: any) => {
      toast.error(error?.response?.data?.detail || 'Failed to save system landscape');
    },
  });

  // State for current system being added
  const [currentSystemName, setCurrentSystemName] = useState('');
  const [currentBusinessUse, setCurrentBusinessUse] = useState('');
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [filteredSystems, setFilteredSystems] = useState<string[]>([]);

  // State for inline editing
  const [editingSystemId, setEditingSystemId] = useState<string | null>(null);
  const [editingBusinessUse, setEditingBusinessUse] = useState<string>('');
  const [editingGeneralDescription, setEditingGeneralDescription] = useState<string>('');
  const [editingIndustrySpecificUses, setEditingIndustrySpecificUses] = useState<string>('');
  const [editingTypicalUseCases, setEditingTypicalUseCases] = useState<string>('');
  const [editingKeyFunctionalities, setEditingKeyFunctionalities] = useState<string>('');
  const [editingIntegrationPoints, setEditingIntegrationPoints] = useState<string>('');
  const [savingSystemId, setSavingSystemId] = useState<string | null>(null);

  // State for data requirement mappings
  const [systemMappings, setSystemMappings] = useState<Record<string, any[]>>({});
  const [loadingMappingsFor, setLoadingMappingsFor] = useState<string | null>(null);

  // State for editing mappings
  const [editingMappingId, setEditingMappingId] = useState<string | null>(null);
  const [editingMappingSystemId, setEditingMappingSystemId] = useState<string | null>(null);
  const [editingMappingData, setEditingMappingData] = useState<{
    entity: string;
    category: 'master_data' | 'transactional_data';
    confidence: 'high' | 'medium' | 'low';
    reasoning: string;
    typicalLocation: string;
  } | null>(null);

  // State for additional data suggestions modal
  const [showAdditionalDataModal, setShowAdditionalDataModal] = useState(false);
  const [additionalDataSystemName, setAdditionalDataSystemName] = useState<string>('');
  const [isResearchingAdditionalData, setIsResearchingAdditionalData] = useState(false);

  const handleSystemNameChange = (value: string) => {
    setCurrentSystemName(value);

    if (value.trim().length > 0) {
      const filtered = ENTERPRISE_SYSTEMS.filter((sys) =>
        sys.toLowerCase().includes(value.toLowerCase())
      );
      setFilteredSystems(filtered);
      setShowSuggestions(true);
    } else {
      setShowSuggestions(false);
    }
  };

  const handleSelectSuggestion = (systemName: string) => {
    setCurrentSystemName(systemName);
    setShowSuggestions(false);
  };

  const handleResearchSystem = async () => {
    if (!currentSystemName.trim()) {
      toast.error('Please enter a system name first');
      return;
    }

    if (!caseId) {
      toast.error('Project ID is required for AI research');
      return;
    }

    setIsResearching(true);
    setShowResearchResults(false);

    try {
      const result = await api.post('/system-research', {
        systemName: currentSystemName.trim(),
        caseId: caseId
      });

      setResearchResults(result);
      setShowResearchResults(true);

      // Auto-fill the business use field with AI suggestion
      setCurrentBusinessUse(result.suggestedBusinessUse);

      // Initialize selected mappings with all AI-identified mappings
      setSelectedMappings(result.dataRequirementMappings || []);

      toast.success('AI research completed! Review and edit the suggested business use and data mappings.');
    } catch (error: any) {
      toast.error(error?.response?.data?.detail || 'Failed to research system');
      console.error('Research error:', error);
    } finally {
      setIsResearching(false);
    }
  };

  const handleRemoveMapping = (mappingToRemove: DataRequirementMapping) => {
    setSelectedMappings(selectedMappings.filter(m => m.requirementEntity !== mappingToRemove.requirementEntity));
  };

  const toggleSystemExpanded = (systemId: string) => {
    setExpandedSystems(prev => {
      const newSet = new Set(prev);
      if (newSet.has(systemId)) {
        newSet.delete(systemId);
      } else {
        newSet.add(systemId);
      }
      return newSet;
    });
  };

  const handleAddSystem = () => {
    if (!currentSystemName.trim() || !currentBusinessUse.trim()) {
      return;
    }

    const newSystem: EnterpriseSystem = {
      id: Date.now().toString(),
      name: currentSystemName.trim(),
      businessUse: currentBusinessUse.trim(),
    };

    // Include AI research data if available
    if (researchResults) {
      newSystem.generalDescription = researchResults.generalDescription;
      newSystem.typicalUseCases = researchResults.typicalUseCases;
      newSystem.industrySpecificUses = researchResults.industrySpecificUses;
      newSystem.keyFunctionalities = researchResults.keyFunctionalities;
      newSystem.integrationPoints = researchResults.integrationPoints;
      // Use selected mappings (user may have removed some)
      newSystem.dataRequirementMappings = selectedMappings;
    }

    setSystems([...systems, newSystem]);
    setHasUnsavedChanges(true);
    setCurrentSystemName('');
    setCurrentBusinessUse('');
    setShowSuggestions(false);
    setShowResearchResults(false);
    setResearchResults(null);
    setSelectedMappings([]); // Clear selected mappings
  };

  const handleRemoveSystem = (id: string) => {
    setSystems(systems.filter((sys) => sys.id !== id));
    setHasUnsavedChanges(true);
  };

  const handleStartEdit = (system: EnterpriseSystem) => {
    setEditingSystemId(system.id);
    setEditingBusinessUse(system.businessUse);
    setEditingGeneralDescription(system.generalDescription || '');
    setEditingIndustrySpecificUses(system.industrySpecificUses || '');
    // Convert arrays to newline-separated strings for editing
    setEditingTypicalUseCases(system.typicalUseCases?.join('\n') || '');
    setEditingKeyFunctionalities(system.keyFunctionalities?.join('\n') || '');
    setEditingIntegrationPoints(system.integrationPoints?.join('\n') || '');
  };

  const handleCancelEdit = () => {
    setEditingSystemId(null);
    setEditingBusinessUse('');
    setEditingGeneralDescription('');
    setEditingIndustrySpecificUses('');
    setEditingTypicalUseCases('');
    setEditingKeyFunctionalities('');
    setEditingIntegrationPoints('');
  };

  const handleSaveEdit = async (id: string) => {
    if (!editingBusinessUse.trim()) {
      toast.error('Business use cannot be empty');
      return;
    }

    if (!caseId) {
      toast.error('Project ID is required to save changes');
      return;
    }

    setSavingSystemId(id);

    // Convert newline-separated strings back to arrays
    const typicalUseCases = editingTypicalUseCases
      .split('\n')
      .map(line => line.trim())
      .filter(line => line.length > 0);

    const keyFunctionalities = editingKeyFunctionalities
      .split('\n')
      .map(line => line.trim())
      .filter(line => line.length > 0);

    const integrationPoints = editingIntegrationPoints
      .split('\n')
      .map(line => line.trim())
      .filter(line => line.length > 0);

    // Update local state
    const updatedSystems = systems.map(sys =>
      sys.id === id
        ? {
            ...sys,
            businessUse: editingBusinessUse.trim(),
            generalDescription: editingGeneralDescription.trim() || undefined,
            industrySpecificUses: editingIndustrySpecificUses.trim() || undefined,
            typicalUseCases: typicalUseCases.length > 0 ? typicalUseCases : undefined,
            keyFunctionalities: keyFunctionalities.length > 0 ? keyFunctionalities : undefined,
            integrationPoints: integrationPoints.length > 0 ? integrationPoints : undefined
          }
        : sys
    );
    setSystems(updatedSystems);

    // Save to Neo4j
    try {
      await api.post(`/cases/${caseId}/system-landscape`, {
        systems: updatedSystems.map(s => ({
          name: s.name,
          businessUse: s.businessUse,
          generalDescription: s.generalDescription,
          typicalUseCases: s.typicalUseCases,
          industrySpecificUses: s.industrySpecificUses,
          keyFunctionalities: s.keyFunctionalities,
          integrationPoints: s.integrationPoints,
          dataRequirementMappings: s.dataRequirementMappings
        }))
      });

      toast.success('System updated successfully!');
      queryClient.invalidateQueries({ queryKey: ['systemLandscape', caseId] });

      setEditingSystemId(null);
      setEditingBusinessUse('');
      setEditingGeneralDescription('');
      setEditingIndustrySpecificUses('');
      setEditingTypicalUseCases('');
      setEditingKeyFunctionalities('');
      setEditingIntegrationPoints('');
    } catch (error: any) {
      toast.error(error?.response?.data?.detail || 'Failed to update system');
      // Revert local state on error
      setSystems(systems);
    } finally {
      setSavingSystemId(null);
    }
  };

  const handleSave = async () => {
    if (!caseId) {
      toast.error('Project ID is required to save system landscape');
      return;
    }

    saveMutation.mutate(systems);
  };

  // Load mappings for a system
  const loadMappingsForSystem = async (systemName: string) => {
    if (!caseId) return;

    setLoadingMappingsFor(systemName);
    try {
      const mappings = await api.get(`/system-research/mappings?caseId=${caseId}&systemName=${encodeURIComponent(systemName)}`);
      setSystemMappings(prev => ({ ...prev, [systemName]: mappings }));
    } catch (error: any) {
      console.error('Error loading mappings:', error);
      // Don't show error toast, just log it
    } finally {
      setLoadingMappingsFor(null);
    }
  };

  // Delete a mapping
  const handleDeleteMapping = async (systemName: string, mappingId: string) => {
    try {
      await api.delete(`/system-research/mappings/${mappingId}`);

      // Remove from local state
      setSystemMappings(prev => ({
        ...prev,
        [systemName]: prev[systemName]?.filter(m => m.mappingId !== mappingId) || []
      }));

      // Also update the systems array to remove from dataRequirementMappings
      setSystems(prevSystems =>
        prevSystems.map(sys => {
          if (sys.name === systemName && sys.dataRequirementMappings) {
            return {
              ...sys,
              dataRequirementMappings: sys.dataRequirementMappings.filter(
                (m: any) => m.mappingId !== mappingId
              )
            };
          }
          return sys;
        })
      );

      toast.success('Mapping removed');
      queryClient.invalidateQueries({ queryKey: ['systemLandscape', caseId] });
    } catch (error: any) {
      toast.error('Failed to remove mapping');
    }
  };

  // Start editing a mapping
  const handleStartEditMapping = (systemId: string, mapping: any) => {
    setEditingMappingId(mapping.mappingId);
    setEditingMappingSystemId(systemId);
    setEditingMappingData({
      entity: mapping.requirementEntity,
      category: mapping.requirementCategory,
      confidence: mapping.confidence,
      reasoning: mapping.reasoning || '',
      typicalLocation: mapping.typicalLocation || ''
    });
  };

  // Cancel editing mapping
  const handleCancelEditMapping = () => {
    setEditingMappingId(null);
    setEditingMappingSystemId(null);
    setEditingMappingData(null);
  };

  // Save edited mapping
  const handleSaveEditMapping = async () => {
    if (!editingMappingData || !editingMappingId || !caseId) return;

    try {
      // Update via API
      await api.patch(`/system-research/mappings/${editingMappingId}`, {
        confidence: editingMappingData.confidence,
        reasoning: editingMappingData.reasoning,
        typicalLocation: editingMappingData.typicalLocation
      });

      toast.success('Mapping updated successfully');

      // Refresh the system landscape data
      queryClient.invalidateQueries({ queryKey: ['systemLandscape', caseId] });

      // Clear editing state
      handleCancelEditMapping();
    } catch (error: any) {
      toast.error(error?.response?.data?.detail || 'Failed to update mapping');
    }
  };

  // Open additional data suggestions modal
  const handleOpenAdditionalDataModal = (systemName: string) => {
    setAdditionalDataSystemName(systemName);
    setShowAdditionalDataModal(true);
  };

  // Research additional data with user hints
  const handleResearchAdditionalData = async (userHints: string): Promise<DataRequirementMapping[]> => {
    if (!caseId) {
      toast.error('Project ID is required');
      return [];
    }

    setIsResearchingAdditionalData(true);
    try {
      const result = await api.post('/system-research/additional-data', {
        systemName: additionalDataSystemName,
        caseId: caseId,
        userHints: userHints
      });

      return result.suggestions || [];
    } catch (error: any) {
      toast.error(error?.response?.data?.detail || 'Failed to research additional data');
      throw error;
    } finally {
      setIsResearchingAdditionalData(false);
    }
  };

  // Approve and save additional data suggestions
  const handleApproveAdditionalData = async (approvedSuggestions: DataRequirementMapping[]) => {
    if (!caseId) {
      toast.error('Project ID is required');
      return;
    }

    try {
      // Save approved mappings
      await api.post('/system-research/mappings', {
        caseId: caseId,
        systemName: additionalDataSystemName,
        mappings: approvedSuggestions
      });

      toast.success(`Added ${approvedSuggestions.length} data ${approvedSuggestions.length === 1 ? 'source' : 'sources'} to ${additionalDataSystemName}`);

      // Refresh the system landscape data
      queryClient.invalidateQueries({ queryKey: ['systemLandscape', caseId] });

      setShowAdditionalDataModal(false);
    } catch (error: any) {
      toast.error(error?.response?.data?.detail || 'Failed to save additional data');
      throw error;
    }
  };

  // Load mappings when systems change
  useEffect(() => {
    if (caseId && systems.length > 0) {
      systems.forEach(sys => {
        if (!systemMappings[sys.name]) {
          loadMappingsForSystem(sys.name);
        }
      });
    }
  }, [systems, caseId]);

  // Warn before leaving page with unsaved changes
  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (hasUnsavedChanges) {
        e.preventDefault();
        e.returnValue = '';
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [hasUnsavedChanges]);

  // Expose a method to handle navigation with save prompt
  // This will be called by parent component when "Next" is clicked
  useEffect(() => {
    if (onComplete) {
      // Attach the check to the window object so parent can call it
      (window as any).checkUnsavedSystemLandscape = () => {
        if (hasUnsavedChanges) {
          const shouldSave = window.confirm('You have unsaved changes in System Landscape. Would you like to save before continuing?');
          if (shouldSave) {
            handleSave();
            return new Promise((resolve) => {
              // Wait for save to complete
              const checkSaved = setInterval(() => {
                if (!saveMutation.isPending) {
                  clearInterval(checkSaved);
                  resolve(true);
                }
              }, 100);
            });
          }
        }
        return Promise.resolve(true);
      };
    }
    return () => {
      delete (window as any).checkUnsavedSystemLandscape;
    };
  }, [hasUnsavedChanges, onComplete]);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Spinner size="lg" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Data Sourcing Overview */}
      {caseId && systems.length > 0 && (
        <DataSourcingOverview caseId={caseId} />
      )}

      {/* Add System Section */}
      <Card>
            <div className="p-4 space-y-4">
              <h3 className="text-lg font-semibold text-neutral-900">
                Add Enterprise System
              </h3>

              <div className="relative">
                <Input
                  label="System Name"
                  value={currentSystemName}
                  onChange={(e) => handleSystemNameChange(e.target.value)}
                  placeholder="Start typing to search (e.g., SAP, Oracle, Salesforce)..."
                  helperText="Select from common systems or enter a custom name"
                />

                {/* Suggestions Dropdown */}
                {showSuggestions && filteredSystems.length > 0 && (
                  <div className="absolute z-10 w-full mt-1 bg-white border border-neutral-300 rounded-lg shadow-lg max-h-60 overflow-y-auto">
                    {filteredSystems.map((system) => (
                      <button
                        key={system}
                        type="button"
                        onClick={() => handleSelectSuggestion(system)}
                        className="w-full px-4 py-2 text-left hover:bg-primary-50 focus:bg-primary-50 focus:outline-none transition-colors text-sm"
                      >
                        <div className="flex items-center gap-2">
                          <Search className="w-4 h-4 text-neutral-400" />
                          <span className="text-neutral-900">{system}</span>
                        </div>
                      </button>
                    ))}
                  </div>
                )}
              </div>

              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <label className="block text-sm font-medium text-neutral-700">
                    Business Use
                  </label>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handleResearchSystem}
                    disabled={!currentSystemName.trim() || isResearching}
                    loading={isResearching}
                  >
                    <Sparkles className="w-4 h-4" />
                    {isResearching ? 'Researching...' : 'Research with Amplinth'}
                  </Button>
                </div>

                <Textarea
                  value={currentBusinessUse}
                  onChange={(e) => setCurrentBusinessUse(e.target.value)}
                  placeholder="Describe what business processes this system supports...

Example:
• Primary ERP for financial management, procurement, and inventory
• Handles all order-to-cash processes
• Integration with manufacturing execution systems
• Source of truth for product master data"
                  rows={6}
                  helperText="Click 'Research with Amplinth' to get contextual suggestions, or write your own description"
                />

                {/* AI Research Results */}
                {showResearchResults && researchResults && (
                  <div className="mt-3 space-y-3">
                    <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg space-y-3">
                      <div className="flex items-start gap-2">
                        <Sparkles className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
                        <div className="flex-1 space-y-3">
                          <div>
                            <h4 className="text-sm font-semibold text-blue-900 mb-1">AI Research Results</h4>
                            <p className="text-xs text-blue-700">{researchResults.generalDescription}</p>
                          </div>

                          {researchResults.industrySpecificUses && (
                            <div>
                              <h5 className="text-xs font-semibold text-blue-800 mb-1">Industry-Specific Usage:</h5>
                              <p className="text-xs text-blue-700">{researchResults.industrySpecificUses}</p>
                            </div>
                          )}

                          {researchResults.keyFunctionalities && researchResults.keyFunctionalities.length > 0 && (
                            <div>
                              <h5 className="text-xs font-semibold text-blue-800 mb-1">Key Functionalities:</h5>
                              <ul className="list-disc list-inside text-xs text-blue-700 space-y-0.5">
                                {researchResults.keyFunctionalities.slice(0, 3).map((func: string, idx: number) => (
                                  <li key={idx}>{func}</li>
                                ))}
                              </ul>
                            </div>
                          )}

                          <p className="text-xs text-blue-600 italic">
                            💡 The suggested business use has been pre-filled above. Review and edit as needed.
                          </p>
                        </div>
                      </div>
                    </div>

                    {/* Data Requirements Mappings */}
                    {selectedMappings.length > 0 && (
                      <div className="p-4 bg-green-50 border border-green-200 rounded-lg">
                        <div className="flex items-start gap-2 mb-3">
                          <TrendingUp className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                          <div className="flex-1">
                            <h4 className="text-sm font-semibold text-green-900 mb-1">
                              Data Mappings ({selectedMappings.length})
                            </h4>
                            <p className="text-xs text-green-700 mb-3">
                              Based on your project's data requirements, these entities will be mapped to {currentSystemName}. Click × to remove unwanted mappings.
                            </p>
                          </div>
                        </div>

                        <div className="space-y-2">
                          {selectedMappings.map((mapping: any, idx: number) => (
                            <div key={idx} className="bg-white rounded-md p-3 border border-green-200">
                              <div className="flex items-start justify-between gap-3">
                                <div className="flex items-start gap-2 flex-1">
                                  {mapping.requirementCategory === 'master_data' ? (
                                    <Database className="w-4 h-4 text-primary-600 flex-shrink-0 mt-0.5" />
                                  ) : (
                                    <FileText className="w-4 h-4 text-secondary-600 flex-shrink-0 mt-0.5" />
                                  )}
                                  <div className="flex-1 min-w-0">
                                    <div className="flex items-center gap-2 mb-1">
                                      <span className="text-sm font-semibold text-neutral-900">
                                        {mapping.requirementEntity}
                                      </span>
                                      <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${
                                        mapping.confidence === 'high'
                                          ? 'bg-green-100 text-green-700'
                                          : mapping.confidence === 'medium'
                                          ? 'bg-amber-100 text-amber-700'
                                          : 'bg-blue-100 text-blue-700'
                                      }`}>
                                        {mapping.confidence.toUpperCase()} confidence
                                      </span>
                                    </div>
                                    <p className="text-xs text-neutral-600 mb-1">
                                      {mapping.reasoning}
                                    </p>
                                    {mapping.typicalLocation && (
                                      <p className="text-xs text-neutral-500 italic">
                                        📍 Typical location: {mapping.typicalLocation}
                                      </p>
                                    )}
                                  </div>
                                </div>
                                <button
                                  onClick={() => handleRemoveMapping(mapping)}
                                  className="flex-shrink-0 p-1 text-red-600 hover:text-red-700 hover:bg-red-50 rounded transition-colors"
                                  title="Remove this mapping"
                                >
                                  <X className="w-4 h-4" />
                                </button>
                              </div>
                            </div>
                          ))}
                        </div>

                        <p className="text-xs text-green-600 italic mt-3">
                          ℹ️ These mappings are AI-generated suggestions. Click × to remove any that don't apply.
                        </p>
                      </div>
                    )}
                  </div>
                )}
              </div>

              <Button
                onClick={handleAddSystem}
                disabled={!currentSystemName.trim() || !currentBusinessUse.trim()}
              >
                <Plus className="w-4 h-4" />
                Add System
              </Button>
            </div>
          </Card>

          {/* Systems List */}
          {(systems.length > 0 || hasUnsavedChanges) && (
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <h3 className="text-lg font-semibold text-neutral-900">
                    Added Systems ({systems.length})
                  </h3>
                  {hasUnsavedChanges && (
                    <span className="text-xs px-2 py-1 bg-amber-100 text-amber-700 rounded-full font-medium">
                      Unsaved changes
                    </span>
                  )}
                </div>
                <Button
                  onClick={handleSave}
                  disabled={saveMutation.isPending}
                  loading={saveMutation.isPending}
                >
                  <Save className="w-4 h-4" />
                  {hasUnsavedChanges ? 'Save Changes' : 'Save & Get Feedback'}
                </Button>
              </div>

              <div className="space-y-3">
                {systems.map((system) => (
                  <Card key={system.id} className="border-l-4 border-l-primary-500">
                    <div className="p-4">
                      {editingSystemId === system.id ? (
                        // Editing mode
                        <div className="space-y-3">
                          <div>
                            <label className="block text-sm font-medium text-neutral-700 mb-1">
                              System Name (read-only)
                            </label>
                            <div className="px-3 py-2 bg-neutral-100 border border-neutral-300 rounded-lg text-neutral-900 font-semibold">
                              {system.name}
                            </div>
                          </div>

                          <div>
                            <label className="block text-sm font-medium text-neutral-700 mb-1">
                              Business Use *
                            </label>
                            <Textarea
                              value={editingBusinessUse}
                              onChange={(e) => setEditingBusinessUse(e.target.value)}
                              rows={4}
                              placeholder="Describe how this system is used in your business..."
                            />
                          </div>

                          <div>
                            <label className="block text-sm font-medium text-neutral-700 mb-1">
                              General Description
                            </label>
                            <Textarea
                              value={editingGeneralDescription}
                              onChange={(e) => setEditingGeneralDescription(e.target.value)}
                              rows={3}
                              placeholder="Brief overview of what this system is and its primary purpose..."
                            />
                          </div>

                          <div>
                            <label className="block text-sm font-medium text-neutral-700 mb-1">
                              Industry-Specific Uses
                            </label>
                            <Textarea
                              value={editingIndustrySpecificUses}
                              onChange={(e) => setEditingIndustrySpecificUses(e.target.value)}
                              rows={3}
                              placeholder="How this system is specifically used in your industry..."
                            />
                          </div>

                          <div>
                            <label className="block text-sm font-medium text-neutral-700 mb-1">
                              Typical Use Cases
                              <span className="text-xs text-neutral-500 ml-2">(one per line)</span>
                            </label>
                            <Textarea
                              value={editingTypicalUseCases}
                              onChange={(e) => setEditingTypicalUseCases(e.target.value)}
                              rows={4}
                              placeholder="Enter each use case on a new line..."
                            />
                          </div>

                          <div>
                            <label className="block text-sm font-medium text-neutral-700 mb-1">
                              Key Functionalities
                              <span className="text-xs text-neutral-500 ml-2">(one per line)</span>
                            </label>
                            <Textarea
                              value={editingKeyFunctionalities}
                              onChange={(e) => setEditingKeyFunctionalities(e.target.value)}
                              rows={4}
                              placeholder="Enter each functionality on a new line..."
                            />
                          </div>

                          <div>
                            <label className="block text-sm font-medium text-neutral-700 mb-1">
                              Integration Points
                              <span className="text-xs text-neutral-500 ml-2">(one per line)</span>
                            </label>
                            <Textarea
                              value={editingIntegrationPoints}
                              onChange={(e) => setEditingIntegrationPoints(e.target.value)}
                              rows={3}
                              placeholder="Enter each integration point on a new line..."
                            />
                          </div>

                          <div className="flex items-center gap-2 pt-2">
                            <Button
                              size="sm"
                              onClick={() => handleSaveEdit(system.id)}
                              disabled={!editingBusinessUse.trim() || savingSystemId === system.id}
                              loading={savingSystemId === system.id}
                            >
                              <Check className="w-4 h-4" />
                              Save Changes
                            </Button>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={handleCancelEdit}
                              disabled={savingSystemId === system.id}
                            >
                              <X className="w-4 h-4" />
                              Cancel
                            </Button>
                          </div>
                        </div>
                      ) : (
                        // View mode
                        <div className="space-y-3">
                          {/* Header with expand/collapse */}
                          <div className="flex items-start justify-between gap-4">
                            <div className="flex-1">
                              <button
                                onClick={() => toggleSystemExpanded(system.id)}
                                className="flex items-center gap-2 w-full text-left group"
                              >
                                {expandedSystems.has(system.id) ? (
                                  <ChevronDown className="w-5 h-5 text-neutral-500 flex-shrink-0" />
                                ) : (
                                  <ChevronRight className="w-5 h-5 text-neutral-500 flex-shrink-0" />
                                )}
                                <h4 className="font-semibold text-neutral-900 group-hover:text-primary-600 transition-colors">
                                  {system.name}
                                </h4>
                              </button>

                              {/* Always show a preview of business use */}
                              {!expandedSystems.has(system.id) && (
                                <p className="text-sm text-neutral-600 mt-2 ml-7 line-clamp-2">
                                  {system.businessUse}
                                </p>
                              )}
                            </div>
                            <div className="flex items-center gap-2">
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleStartEdit(system)}
                              >
                                <Pencil className="w-4 h-4 text-primary-600" />
                              </Button>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleRemoveSystem(system.id)}
                              >
                                <Trash2 className="w-4 h-4 text-red-600" />
                              </Button>
                            </div>
                          </div>

                          {/* Expanded details */}
                          {expandedSystems.has(system.id) && (
                            <div className="ml-7 space-y-3 pt-2 border-t border-neutral-200">
                              {/* Business Use */}
                              <div>
                                <h5 className="text-xs font-semibold text-neutral-700 mb-1">Business Use</h5>
                                <p className="text-sm text-neutral-600 whitespace-pre-wrap">
                                  {system.businessUse}
                                </p>
                              </div>

                              {/* General Description */}
                              {system.generalDescription && (
                                <div>
                                  <h5 className="text-xs font-semibold text-neutral-700 mb-1">Description</h5>
                                  <p className="text-sm text-neutral-600">
                                    {system.generalDescription}
                                  </p>
                                </div>
                              )}

                              {/* Industry-Specific Uses */}
                              {system.industrySpecificUses && (
                                <div>
                                  <h5 className="text-xs font-semibold text-neutral-700 mb-1">Industry-Specific Uses</h5>
                                  <p className="text-sm text-neutral-600">
                                    {system.industrySpecificUses}
                                  </p>
                                </div>
                              )}

                              {/* Typical Use Cases */}
                              {system.typicalUseCases && system.typicalUseCases.length > 0 && (
                                <div>
                                  <h5 className="text-xs font-semibold text-neutral-700 mb-1">Typical Use Cases</h5>
                                  <ul className="text-sm text-neutral-600 space-y-1">
                                    {system.typicalUseCases.map((useCase: string, idx: number) => (
                                      <li key={idx} className="flex items-start gap-2">
                                        <span className="text-primary-600 mt-1">•</span>
                                        <span>{useCase}</span>
                                      </li>
                                    ))}
                                  </ul>
                                </div>
                              )}

                              {/* Key Functionalities */}
                              {system.keyFunctionalities && system.keyFunctionalities.length > 0 && (
                                <div>
                                  <h5 className="text-xs font-semibold text-neutral-700 mb-1">Key Functionalities</h5>
                                  <ul className="text-sm text-neutral-600 space-y-1">
                                    {system.keyFunctionalities.map((func: string, idx: number) => (
                                      <li key={idx} className="flex items-start gap-2">
                                        <span className="text-primary-600 mt-1">•</span>
                                        <span>{func}</span>
                                      </li>
                                    ))}
                                  </ul>
                                </div>
                              )}

                              {/* Integration Points */}
                              {system.integrationPoints && system.integrationPoints.length > 0 && (
                                <div>
                                  <h5 className="text-xs font-semibold text-neutral-700 mb-1">Integration Points</h5>
                                  <div className="flex flex-wrap gap-2">
                                    {system.integrationPoints.map((point: string, idx: number) => (
                                      <span
                                        key={idx}
                                        className="text-xs px-2 py-1 bg-blue-100 text-blue-700 rounded-full"
                                      >
                                        {point}
                                      </span>
                                    ))}
                                  </div>
                                </div>
                              )}

                              {/* Data Requirements Mappings for this system */}
                              {(() => {
                                // Show mappings from either the system object (before save) or systemMappings (after save)
                                const mappings = system.dataRequirementMappings || systemMappings[system.name] || [];
                                return (
                                  <div>
                                    <div className="flex items-center justify-between mb-1">
                                      <h5 className="text-xs font-semibold text-neutral-700 flex items-center gap-2">
                                        <TrendingUp className="w-4 h-4 text-green-600" />
                                        Available Data ({mappings.length})
                                      </h5>
                                      <button
                                        onClick={() => handleOpenAdditionalDataModal(system.name)}
                                        className="flex items-center gap-1 px-2 py-1 text-xs font-medium text-primary-600 hover:text-primary-700 hover:bg-primary-50 rounded transition-colors"
                                        title="Add data mappings for this system"
                                      >
                                        <Plus className="w-3 h-3" />
                                        Add Data Mapping
                                      </button>
                                    </div>
                                    {mappings.length > 0 ? (
                                      <div className="space-y-1.5">
                                        {mappings.map((mapping: any, idx: number) => {
                                        const isEditing = editingMappingId === mapping.mappingId && editingMappingSystemId === system.id;

                                        return (
                                          <div
                                            key={mapping.mappingId || `mapping-${idx}`}
                                            className={`rounded px-2 py-1.5 ${
                                              isEditing
                                                ? 'bg-blue-50 border border-blue-300'
                                                : mapping.matchesExisting
                                                ? 'bg-green-50 border border-green-200 group hover:bg-neutral-100 transition-colors'
                                                : 'bg-neutral-50 group hover:bg-neutral-100 transition-colors'
                                            }`}
                                          >
                                            {isEditing ? (
                                              // Edit mode
                                              <div className="space-y-2">
                                                <div className="flex items-center gap-2">
                                                  <span className="text-xs font-semibold text-neutral-900">
                                                    {editingMappingData?.entity}
                                                  </span>
                                                  <span className={`text-[10px] px-1.5 py-0.5 rounded font-medium ${
                                                    editingMappingData?.category === 'master_data'
                                                      ? 'bg-purple-100 text-purple-700 border border-purple-200'
                                                      : 'bg-orange-100 text-orange-700 border border-orange-200'
                                                  }`}>
                                                    {editingMappingData?.category === 'master_data' ? 'Master Data' : 'Transactional'}
                                                  </span>
                                                </div>

                                                {/* Confidence dropdown */}
                                                <div>
                                                  <label className="block text-[10px] font-medium text-neutral-700 mb-0.5">
                                                    Confidence
                                                  </label>
                                                  <select
                                                    value={editingMappingData?.confidence}
                                                    onChange={(e) => setEditingMappingData(prev => prev ? {
                                                      ...prev,
                                                      confidence: e.target.value as 'high' | 'medium' | 'low'
                                                    } : null)}
                                                    className="w-full text-xs px-2 py-1 border border-neutral-300 rounded focus:ring-1 focus:ring-primary-500"
                                                  >
                                                    <option value="high">High</option>
                                                    <option value="medium">Medium</option>
                                                    <option value="low">Low</option>
                                                  </select>
                                                </div>

                                                {/* Reasoning textarea */}
                                                <div>
                                                  <label className="block text-[10px] font-medium text-neutral-700 mb-0.5">
                                                    Reasoning
                                                  </label>
                                                  <textarea
                                                    value={editingMappingData?.reasoning}
                                                    onChange={(e) => setEditingMappingData(prev => prev ? {
                                                      ...prev,
                                                      reasoning: e.target.value
                                                    } : null)}
                                                    rows={2}
                                                    className="w-full text-[10px] px-2 py-1 border border-neutral-300 rounded focus:ring-1 focus:ring-primary-500 resize-none"
                                                    placeholder="Why this data is available in this system..."
                                                  />
                                                </div>

                                                {/* Typical location input */}
                                                <div>
                                                  <label className="block text-[10px] font-medium text-neutral-700 mb-0.5">
                                                    Typical Location (optional)
                                                  </label>
                                                  <input
                                                    type="text"
                                                    value={editingMappingData?.typicalLocation}
                                                    onChange={(e) => setEditingMappingData(prev => prev ? {
                                                      ...prev,
                                                      typicalLocation: e.target.value
                                                    } : null)}
                                                    className="w-full text-[10px] px-2 py-1 border border-neutral-300 rounded focus:ring-1 focus:ring-primary-500"
                                                    placeholder="e.g., MM Module - MARA table"
                                                  />
                                                </div>

                                                {/* Action buttons */}
                                                <div className="flex items-center gap-1 pt-1">
                                                  <button
                                                    onClick={handleSaveEditMapping}
                                                    className="flex items-center gap-1 px-2 py-1 bg-primary-600 text-white text-[10px] font-medium rounded hover:bg-primary-700 transition-colors"
                                                  >
                                                    <Check className="w-3 h-3" />
                                                    Save
                                                  </button>
                                                  <button
                                                    onClick={handleCancelEditMapping}
                                                    className="flex items-center gap-1 px-2 py-1 bg-neutral-200 text-neutral-700 text-[10px] font-medium rounded hover:bg-neutral-300 transition-colors"
                                                  >
                                                    <X className="w-3 h-3" />
                                                    Cancel
                                                  </button>
                                                </div>
                                              </div>
                                            ) : (
                                              // View mode
                                              <div className="flex items-start gap-2">
                                                {mapping.requirementCategory === 'master_data' ? (
                                                  <Database className="w-3.5 h-3.5 text-primary-600 flex-shrink-0 mt-0.5" />
                                                ) : (
                                                  <FileText className="w-3.5 h-3.5 text-secondary-600 flex-shrink-0 mt-0.5" />
                                                )}
                                                <div className="flex-1 min-w-0">
                                                  <div className="flex items-center gap-1.5 flex-wrap">
                                                    <span className="text-xs font-medium text-neutral-900">
                                                      {mapping.requirementEntity}
                                                    </span>
                                                    <span className={`text-[10px] px-1.5 py-0.5 rounded-full font-medium ${
                                                      mapping.confidence === 'high'
                                                        ? 'bg-green-100 text-green-700'
                                                        : mapping.confidence === 'medium'
                                                        ? 'bg-amber-100 text-amber-700'
                                                        : 'bg-blue-100 text-blue-700'
                                                    }`}>
                                                      {mapping.confidence}
                                                    </span>
                                                    <span className={`text-[10px] px-1.5 py-0.5 rounded font-medium ${
                                                      mapping.requirementCategory === 'master_data'
                                                        ? 'bg-purple-100 text-purple-700 border border-purple-200'
                                                        : 'bg-orange-100 text-orange-700 border border-orange-200'
                                                    }`}>
                                                      {mapping.requirementCategory === 'master_data' ? 'Master Data' : 'Transactional'}
                                                    </span>
                                                    {mapping.matchesExisting ? (
                                                      <span className="flex items-center gap-0.5 text-[10px] px-1.5 py-0.5 rounded-full font-medium bg-green-100 text-green-700 border border-green-300" title="Matches existing data requirement from Data Requirements Analysis - will link to existing node in Neo4j">
                                                        <Check className="w-3 h-3" />
                                                        Exists in Neo4j
                                                      </span>
                                                    ) : (
                                                      <span className="flex items-center gap-0.5 text-[10px] px-1.5 py-0.5 rounded-full font-medium bg-blue-100 text-blue-700 border border-blue-300" title="New data requirement - will create new node in Neo4j">
                                                        New in Neo4j
                                                      </span>
                                                    )}
                                                  </div>
                                                  {mapping.reasoning && (
                                                    <p className="text-[10px] text-neutral-600 mt-0.5">
                                                      {mapping.reasoning}
                                                    </p>
                                                  )}
                                                  {mapping.typicalLocation && (
                                                    <p className="text-[10px] text-neutral-500 mt-0.5 italic">
                                                      📍 {mapping.typicalLocation}
                                                    </p>
                                                  )}
                                                </div>
                                                <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                                                  <button
                                                    onClick={() => handleStartEditMapping(system.id, mapping)}
                                                    className="p-0.5 hover:bg-blue-100 rounded"
                                                    title="Edit mapping"
                                                  >
                                                    <Pencil className="w-3.5 h-3.5 text-blue-600" />
                                                  </button>
                                                  {mapping.mappingId && (
                                                    <button
                                                      onClick={() => handleDeleteMapping(system.name, mapping.mappingId)}
                                                      className="p-0.5 hover:bg-red-100 rounded"
                                                      title="Remove mapping"
                                                    >
                                                      <X className="w-3.5 h-3.5 text-red-600" />
                                                    </button>
                                                  )}
                                                </div>
                                              </div>
                                            )}
                                          </div>
                                        );
                                      })}
                                      </div>
                                    ) : (
                                      <p className="text-xs text-neutral-500 italic mt-1">
                                        No data mappings yet. Click "Add Data Mapping" to start.
                                      </p>
                                    )}
                                  </div>
                                );
                              })()}

                              {loadingMappingsFor === system.name && (
                                <div className="flex items-center gap-2 text-xs text-neutral-500">
                                  <Spinner size="sm" />
                                  Loading data mappings...
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  </Card>
                ))}
              </div>
            </div>
          )}

      {/* AI Feedback Section */}
      {aiFeedback && (
        <Card
          className={`border-l-4 ${
            aiFeedback.type === 'success'
              ? 'border-l-green-500 bg-green-50'
              : aiFeedback.type === 'clarification'
              ? 'border-l-amber-500 bg-amber-50'
              : 'border-l-blue-500 bg-blue-50'
          }`}
        >
          <div className="p-4">
            <div className="flex items-start gap-3">
              <Sparkles
                className={`w-5 h-5 flex-shrink-0 mt-0.5 ${
                  aiFeedback.type === 'success'
                    ? 'text-green-600'
                    : aiFeedback.type === 'clarification'
                    ? 'text-amber-600'
                    : 'text-blue-600'
                }`}
              />
              <div className="flex-1">
                <h4
                  className={`text-sm font-semibold mb-1 ${
                    aiFeedback.type === 'success'
                      ? 'text-green-900'
                      : aiFeedback.type === 'clarification'
                      ? 'text-amber-900'
                      : 'text-blue-900'
                  }`}
                >
                  {aiFeedback.type === 'success'
                    ? 'AI Validation Complete'
                    : aiFeedback.type === 'clarification'
                    ? 'AI Needs Clarification'
                    : 'AI Suggestion'}
                </h4>
                <p
                  className={`text-sm ${
                    aiFeedback.type === 'success'
                      ? 'text-green-800'
                      : aiFeedback.type === 'clarification'
                      ? 'text-amber-800'
                      : 'text-blue-800'
                  }`}
                >
                  {aiFeedback.message}
                </p>
              </div>
            </div>
          </div>
        </Card>
      )}

      {/* Helper Tips */}
      {systems.length === 0 && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <h4 className="text-sm font-semibold text-blue-900 mb-2">💡 Tips for better results:</h4>
          <ul className="text-sm text-blue-800 space-y-1 list-disc list-inside">
            <li>Add all key enterprise systems that store or process relevant data</li>
            <li>Be specific about the business processes each system supports</li>
            <li>Mention which systems are sources of truth for specific data domains</li>
            <li>Include integration points and data flows between systems</li>
          </ul>
        </div>
      )}

      {/* Additional Data Suggestions Modal */}
      <AdditionalDataSuggestionsModal
        isOpen={showAdditionalDataModal}
        onClose={() => setShowAdditionalDataModal(false)}
        systemName={additionalDataSystemName}
        caseId={caseId || ''}
        onApprove={handleApproveAdditionalData}
        isResearching={isResearchingAdditionalData}
        onResearch={handleResearchAdditionalData}
      />
    </div>
  );
}
